import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import {
  ActivityLogSvg,
  AddimageSvg,
  AddSvg,
  PromotionsHistorySvg,
  SettingSvg,
  ShortDownSvg,
  ShortUpSvg,
} from "../../../../components/CommonSvg";
import CommonPagination from "../../../../components/Common/TableView/CommonPagination";
import CustomisedModal from "../../../../components/Modal/CustomisedModal";
import VendorLogoModal from "./VendorLogoModal";
import VendorUserColumns from "./VendorUserColumns";
import {
  getZohoVendorListRequest,
  removeVendorLogoRequest,
  uploadVendorLogoRequest,
} from "../../../../redux/admin/vendor/action";
import {
  getVendorUserColumnsRequest,
  setVendorUserColumnsRequest,
} from "../../../../redux/vendor/memberColumns/action";
import Nodata from "../../../../assets/images/no_data_found001.webp";
import { vendorLogoUrl } from "../../../../utils/generalUtils";

function VendorList({
  getZohoVendorList,
  uploadVendorLogo,
  removeVendorLogo,
  getVendorUserColumns,
  setVendorUserColumns,
  vendorUserColumns,
  vendorList,
  totalRecords,
  values,
}) {
  const navigate = useNavigate();
  /* The list is a route of its own, so opening a vendor's activity log or its
     users unmounts it and takes the page, sort and search with it. Rather than
     lift all of that into redux, it travels with the navigation: out in
     location.state here, back on the sub-page's Back button, and read again on
     the way in - see VendorLogs.js and VendorUsers.js. */
  const restored = useLocation().state?.listState;

  const [currentPage, setCurrentPage] = useState(restored?.page || 1);
  const [dataPerPage, setDataPerPage] = useState(restored?.limit || 10);
  const [orderBy, setOrderBy] = useState(restored?.orderBy || "DESC");
  const [orderKey, setOrderKey] = useState(restored?.orderKey || "createdDate");
  const [logoVendor, setLogoVendor] = useState(null);
  const [columnsVendor, setColumnsVendor] = useState(null);

  const handleColumnsModalToggle = (vendor) => {
    setColumnsVendor(vendor);
    getVendorUserColumns(vendor.id);
  };
  /* everything a sub-page has to give back for the list to look the way it was
     left */
  const listState = {
    page: currentPage,
    limit: dataPerPage,
    orderBy: orderBy,
    orderKey: orderKey,
    search: values.search,
  };

  const dispatchData = {
    searchText: values.search,
    pagination: {
      limit: dataPerPage,
      page: currentPage,
      orderKey: orderKey,
      orderBy: orderBy,
    },
  };
  useEffect(() => {
    if (currentPage && dataPerPage) {
      getZohoVendorList(dispatchData);
    }
  }, [currentPage, dataPerPage, values.search, orderBy, orderKey]);

  /* a new search belongs on page one - but only when the user actually typed
     one, never on the first render, which would discard a restored page */
  const searchSettled = useRef(false);
  useEffect(() => {
    if (!searchSettled.current) {
      searchSettled.current = true;
      return;
    }
    setCurrentPage(1);
  }, [values.search]);

  useEffect(() => {
    if (values.clear === true) {
      getZohoVendorList({
        searchText: "",
        pagination: {
          limit: dataPerPage,
          page: 1,
          orderKey: "createdDate",
          orderBy: "ASC",
        },
      });
      values.setClear(false);
    }
  }, [values.clear]);

  const onChangeOrderBy = (data) => {
    if (orderBy == "ASC") {
      setOrderBy("DESC");
      setOrderKey(data);
    } else {
      setOrderKey(data);
      setOrderBy("ASC");
    }
  };

  return (
    <>
      <div className="zoho_vendor_table">
        {vendorList.length > 0 ? (
          <>
            <ul className="zoho_vendor_head">
              <li className="zoho_vendor_logo">
                <span>Logo</span>
              </li>
              <li className="zoho_vendor_name">
                <span>Vendor name</span>
                <Button
                  className="zoho_vendor_sort"
                  onClick={() => onChangeOrderBy("name")}
                >
                  {orderBy == "ASC" ? <ShortUpSvg /> : <ShortDownSvg />}
                </Button>
              </li>
              <li className="zoho_vendor_email">
                <span>Email</span>
              </li>
              <li className="zoho_vendor_id">
                <span>Zoho Id</span>
              </li>
              <li className="zoho_vendor_action">
                <span></span>
              </li>
            </ul>
            {vendorList.map((data, index) => {
              return (
                <ul
                  className="zoho_vendor_row"
                  key={index}
                  title="Manage portal logo"
                  onClick={() => setLogoVendor(data)}
                >
                  <li className="zoho_vendor_logo">
                    <Button
                      className={`zoho_vendor_logo_cell${
                        data.logo ? "" : " is_empty"
                      }`}
                      title={
                        data.logo ? "Manage portal logo" : "Upload portal logo"
                      }
                      onClick={() => setLogoVendor(data)}
                    >
                      {data.logo ? (
                        <img
                          className="zoho_vendor_logo_img"
                          src={vendorLogoUrl(data.logo)}
                          alt={data.name}
                        />
                      ) : (
                        <span className="zoho_vendor_logo_placeholder">
                          <AddimageSvg />
                        </span>
                      )}
                    </Button>
                  </li>
                  <li className="zoho_vendor_name">
                    <p>{data.name}</p>
                  </li>
                  <li className="zoho_vendor_email">
                    <p>{data.emailAddress}</p>
                  </li>
                  <li className="zoho_vendor_id">
                    <p>{data.vendorZohoId}</p>
                  </li>
                  <li className="zoho_vendor_action">
                    <Button
                      className="zoho_vendor_log"
                      title="Activity log"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/vendor-logs/${data.id}`, {
                          state: { ...data, listState: listState },
                        });
                      }}
                    >
                      <PromotionsHistorySvg />
                    </Button>
                    <Button
                      className="zoho_vendor_log"
                      title="Column Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColumnsModalToggle(data);
                      }}
                    >
                      <SettingSvg />
                    </Button>
                    <Button
                      className="zoho_vendor_add_user"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/vendor-users/${data.id}`, {
                          state: { ...data, listState: listState },
                        });
                      }}
                    >
                       Vendor User
                    </Button>
                  </li>
                </ul>
              );
            })}
          </>
        ) : (
          <div className="zoho_vendor_nodata">
            <img alt="NO Data Found" className="no_data_found" src={Nodata} />
          </div>
        )}
      </div>
      {totalRecords > 0 && (
        <CommonPagination
          dataPerPage={dataPerPage}
          totalData={totalRecords}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setDataPerPage={setDataPerPage}
        />
      )}

      {logoVendor && (
        <VendorLogoModal
          vendor={logoVendor}
          onClose={() => setLogoVendor(null)}
          onUpload={(formData) => uploadVendorLogo({ formData, dispatchData })}
          onRemove={(vendorId) => removeVendorLogo({ vendorId, dispatchData })}
        />
      )}
      {columnsVendor && (
        <CustomisedModal
          modalView={!!columnsVendor}
          customClass={
            "category-modal admin-support-ticket support-request-modal modal-lg"
          }
          scrollable
          modalName={
            <div className="ach_modal_header d-flex justify-content-between w-100">
              Table column configuration
              <span>{columnsVendor.name}</span>
            </div>
          }
          onModalDismiss={() => {
            setColumnsVendor(null);
          }}
        >
          <VendorUserColumns
            vendorId={columnsVendor.id}
            savedColumns={vendorUserColumns}
            setVendorUserColumns={setVendorUserColumns}
            onModalDismiss={() => {
              setColumnsVendor(null);
            }}
          />
        </CustomisedModal>
      )}
    </>
  );
}
const mapStateToProps = ({ vendor, VendorMemberColumns }) => {
  let vendorList =
    vendor?.getZohoVendor?.list?.length > 0 ? vendor.getZohoVendor.list : [];
  let totalRecords = vendor.getZohoVendor?.totalRecords;
  const vendorUserColumns =
    VendorMemberColumns?.userColumns?.list?.columnsSelected || [];
  return { vendorList, totalRecords, vendorUserColumns };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getZohoVendorList: (data) => dispatch(getZohoVendorListRequest(data)),
    uploadVendorLogo: (data) => dispatch(uploadVendorLogoRequest(data)),
    removeVendorLogo: (data) => dispatch(removeVendorLogoRequest(data)),
    getVendorUserColumns: (vendorId) =>
      dispatch(getVendorUserColumnsRequest(vendorId)),
    setVendorUserColumns: (data) => dispatch(setVendorUserColumnsRequest(data)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(VendorList);
